/**
 * Canonicalización de glosas y verificación de identidad numérica.
 *
 * Vive en `@sime/shared` porque apps/api y apps/web DEBEN coincidir byte a byte:
 * `aliasLookup` compara `UPPER(plan_matriz_alias.nombre) = normalizeGlosa(glosa)`,
 * así que dos implementaciones que deriven harían que los alias dejaran de
 * matchear en silencio para todo el sistema.
 *
 * Referencia legacy de normalizeGlosa: ProcessSIMEPMTemplate.php / PlanMatrizTrait.php
 */

/**
 * Normalizes glosa text to match legacy PHP behavior exactly.
 * Used for matching Excel row glosas against plan_matriz.nombre.
 */
export function normalizeGlosa(text: string): string {
  return text
    .replace(/\n/g, ' ')
    .replace(/"/g, '')
    .replace(/\t/g, ' ')
    .replace(/[°º]/g, '')
    .replace(/MTO\s/g, 'MTTO ')
    .replace(/N\s*PISO/gi, 'PISO')
    .replace(/N\s*NIVEL/gi, 'NIVEL')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/**
 * Prefijo de frecuencia de mantención con el que arranca CASI TODO nombre de
 * actividad: `MTTO 6M …`, `MTTO 12M …`, `CONT 12M …`, `CAL 12M …`, `MTTO M/5 …`.
 *
 * Medido contra producción el 2026-09-20: 2945 de los 2948 planes activos del
 * ámbito 2 lo llevan, y hay CERO nombres con un token `<dígitos>M` fuera de esa
 * posición. Por eso el patrón puede anclarse al comienzo sin falsos positivos:
 * el 12 de "CAMARA 12" nunca cae acá, y ése sí es identidad.
 */
const FREQUENCY_PREFIX = /^\s*(?:MTTO|MTO|CONT|CAL)\s+(\d+\s*M|M\s*\/\s*\d+)(?=\s|$)/i;

/**
 * La frecuencia declarada en el prefijo, canonizada (`6M`, `12M`, `M/5`), o
 * null si el nombre no sigue esa convención — el ámbito 4 (MVC) no la usa, y
 * ahí no hay nada que comparar.
 */
export function frequencyOf(text: string): string | null {
  const m = FREQUENCY_PREFIX.exec(text);
  return m?.[1] ? m[1].replace(/\s+/g, '').toUpperCase() : null;
}

/**
 * El mismo texto sin su prefijo de frecuencia. Se usa para que los dígitos de
 * la CADENCIA no entren nunca al conjunto de identidad del EQUIPO.
 *
 * Exportada porque la UI la necesita para lo mismo: resaltar los dígitos del
 * equipo sin acusar al de la cadencia (ver `NumericText`).
 */
export function stripFrequencyPrefix(text: string): string {
  return text.replace(FREQUENCY_PREFIX, '');
}

/**
 * ¿Candidato y glosa piden la misma cadencia?
 *
 * Es una dimensión SEPARADA de la identidad a propósito. Hasta el 2026-09-20
 * esta regla vivía dentro del guard numérico, y de ahí salieron los dos daños
 * del incidente INS-092634: el plan del equipo correcto quedaba "sin
 * corroborar" por su cadencia vieja, y un equipo distinto quedaba "corroborado"
 * por compartir el número de la cadencia nueva. Separadas, cada una dice lo que
 * realmente sabe.
 *
 * ASIMÉTRICA, igual que el subset unidireccional de la identidad numérica:
 *
 *  - el CANDIDATO no declara cadencia → true. No afirma nada que la planilla
 *    pueda contradecir. Es el caso de los ámbitos 4 y 5, donde CERO planes usan
 *    el prefijo (medido en prod 2026-09-20: 0/335 y 0/676), así que esta regla
 *    les resulta inerte por completo.
 *  - la GLOSA no declara cadencia y el candidato SÍ → false. El plan afirma una
 *    cadencia que la planilla no corrobora, que es exactamente la forma de
 *    "identidad sin verificar" que el resto del módulo se niega a adoptar sola.
 *    Devolver true acá reabriría el agujero por la ventana: una glosa con el
 *    prefijo fuera de lugar ("INST MTTO 12M C03…") auto-resolvería al plan 6M
 *    y encima dejaría alias permanente. En los ámbitos que usan el prefijo no
 *    cuesta nada: de las 174 glosas reales medidas en el ámbito 2, 173 lo
 *    traen (la restante es "ASD").
 */
export function frequencyMatches(candidateNombre: string, glosaNormalized: string): boolean {
  const candidato = frequencyOf(candidateNombre);
  if (candidato === null) return true;
  return candidato === frequencyOf(glosaNormalized);
}

/**
 * Extracts the set of numeric identity tokens (maximal digit runs) from a
 * plan_matriz name or a glosa. Leading zeros are stripped so "01" ≡ "1", but
 * each token is kept as a STRING — Number() would collapse distinct >15-digit
 * serials onto the same float and let the guard spuriously corroborate them.
 *
 * OJO: opera sobre el texto COMPLETO, prefijo de frecuencia incluido. Es la
 * primitiva cruda — la que resalta los dígitos en pantalla, por ejemplo. Quien
 * compare IDENTIDAD DE EQUIPO debe usar `missingIdentityTokens`, que descarta
 * la frecuencia antes de comparar.
 */
export function numericTokens(text: string): Set<string> {
  const set = new Set<string>();
  const runs = text.match(/\d+/g);
  if (runs) {
    for (const r of runs) set.add(r.replace(/^0+/, '') || '0');
  }
  return set;
}

/**
 * Numeric tokens the candidate plan ASSERTS that the glosa never mentions.
 *
 * Empty ⇒ corroborated. Non-empty ⇒ the plan claims an identity the planilla
 * does not back up, and the list is exactly what the UI needs to say it out
 * loud: "La planilla dice 501. Este plan es del 504."
 *
 * Order is the candidate's own token order, deduplicated — deterministic, so
 * the rendered copy is stable across renders and testable.
 */
export function missingIdentityTokens(candidateNombre: string, glosaNormalized: string): string[] {
  // La frecuencia se descarta de LOS DOS lados. Del candidato, para no exigirle
  // a la planilla que corrobore una cadencia (el 6 de "MTTO 6M" no es un número
  // de equipo). Y de la glosa, para que su cadencia no pueda corroborar un
  // número de equipo del plan: sin esto, la glosa "MTTO 12M INST SALA
  // ELECTRICA" haría pasar al plan del equipo "SALA 12", que es otra cosa.
  const glosaTokens = numericTokens(stripFrequencyPrefix(glosaNormalized));
  const missing: string[] = [];
  for (const t of numericTokens(stripFrequencyPrefix(candidateNombre))) {
    if (!glosaTokens.has(t)) missing.push(t);
  }
  return missing;
}

/**
 * Guards the two fuzzy auto-resolve paths (high-confidence trgm and shared-pauta
 * convergence) against number-blind false positives.
 *
 * Trigram / full-text similarity is character-n-gram based and effectively blind
 * to a single differing digit: "CÁMARA FLOTACIÓN B 504, A 2, CELDA 3" scores
 * >0.9 against the physically-distinct activity "... B 512, A 2, CELDA 3" and
 * used to silently auto-resolve to the wrong plan_matriz — the real INS JULIO
 * 2026 incident where 11 cameras (banks B504/B511) were mis-assigned to B512.
 *
 * A candidate may only auto-resolve when EVERY numeric token it claims is
 * corroborated by the glosa (candidate ⊆ glosa). This is a ONE-DIRECTIONAL
 * subset check, not set equality: extra numbers in the glosa are legitimate
 * (e.g. a trailing equipment serial the plan name omits — the shared-pauta
 * convergence case), whereas a number the plan asserts but the glosa never
 * mentions is an unverified identity claim. On failure the row degrades to
 * 'unmatched' for manual resolution instead of inventing an activity.
 *
 * Exported so the manual-resolution flow (excel-ingestion `/resolve`) can apply
 * the same check before persisting a plan_matriz_alias — a number-mismatched
 * alias would otherwise auto-resolve every future identical glosa via step 3,
 * silently bypassing this guard.
 *
 * CUIDADO (corroboración vacía): un candidato SIN dígitos corrobora contra
 * cualquier glosa, porque el subset vacío está contenido en todo conjunto. Por
 * eso `true` no alcanza como luz verde en la UI — hay que distinguir "verificado"
 * de "no hay números que verificar" con `numericTokens(candidato).size`.
 * Ver `corroborationKind` en el flujo de resolución manual.
 */
export function identityCorroborated(candidateNombre: string, glosaNormalized: string): boolean {
  return missingIdentityTokens(candidateNombre, glosaNormalized).length === 0;
}

/**
 * Clasificación de cuatro estados que consume la UI de resolución manual.
 * Existe acá (y no en el front) para que el ranking del buscador, el guard del
 * alias y el color que ve el operador no puedan discrepar jamás.
 */
export type CorroborationKind = 'verified' | 'no_numbers' | 'mismatch' | 'frequency_mismatch';

export function corroborationKindOf(
  candidateNombre: string,
  glosaNormalized: string,
): CorroborationKind {
  // Orden deliberado: "es otro equipo" es más grave que "es otra cadencia", y
  // es lo primero que el operador tiene que leer cuando pasan las dos cosas.
  if (missingIdentityTokens(candidateNombre, glosaNormalized).length > 0) return 'mismatch';
  if (!frequencyMatches(candidateNombre, glosaNormalized)) return 'frequency_mismatch';
  // Se cuentan los dígitos SIN la frecuencia: un plan cuyo único número era su
  // cadencia no tiene nada que verificar, y decir "verified" mostrando ese
  // número como prueba de identidad es exactamente la mentira que este
  // rediseño existe para matar.
  return numericTokens(stripFrequencyPrefix(candidateNombre)).size > 0 ? 'verified' : 'no_numbers';
}
